import { categoryRouterSSR } from "@api/router";
import React, { Suspense, useEffect, useState } from "react";
import { seoSettingSSR } from "@api/seo";
import Layout from "@components/layout/Layout";
import * as P from "@components/products/list/Proucts-list-styles";
import SingleProduct from "@components/ecommerce/singleProduct/SingleProduct";
import NoticePagination from "@components/csboard/notice/notice-pagination";
import { usePaginationHandlerModified } from "@hook/products/usePaginationHandler";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ProductsApi } from "@api/products";
import { useRouter } from "next/router";
import CategorySlider from "@components/sliders/Category";

const ProductsPage = () => {
  const router = useRouter(); // Router 인스턴스 추가
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  // URL에서 페이지 번호 가져오기
  useEffect(() => {
    const pathSegments = router?.asPath?.split("/");
    const pageFromUrl = parseInt(pathSegments[pathSegments.length - 2], 10);

    if (!isNaN(pageFromUrl) && pageFromUrl > 0 && pageFromUrl !== page) {
      setPage(pageFromUrl);
    }
  }, [router.asPath]);

  // 상품 데이터를 가져오는 함수
  const fetchProducts = async ({ queryKey }) => {
    const [_, currentPage] = queryKey;

    try {
      const body = {
        modes: "AllListSet",
        page: currentPage,
        page_rows: 20,
      };
      const res = await ProductsApi(body);

      if (res?.Code === "0000") {
        return res.Data.Data;
      } else {
        return [];
      }
    } catch (error) {
      console.error(error?.message);
    }
  };

  // React Query를 사용한 상품 데이터 가져오기
  const { data: productsData, isLoading } = useQuery({
    queryKey: ["products", page],
    queryFn: fetchProducts,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });

  const productItems = productsData?.data || [];

  // 페이지네이션
  const {
    next,
    prev,
    handleActive,
    nextEnd,
    prevStart,
    getPagedProducts,
    getPaginationGroup,
    createPagination,
    currentPage,
    pagination,
    setPagination,
    pages,
    setPages,
    limit,
  } = usePaginationHandlerModified({
    products: productsData || { data: [], total_count: 0, total_page: 0 },
    initialPage: page,
  });
  // 페이지에 표시할 상품 목록 가져오기
  const displayProducts = productItems || [];

  // 페이지 변경 시 URL 업데이트 및 쿼리 무효화
  useEffect(() => {
    if (currentPage !== page) {
      // URL 업데이트
      updateUrlWithPage(currentPage);

      // 페이지 상태 업데이트
      setPage(currentPage);

      // 쿼리 무효화하고 다시 가져오기
      queryClient.invalidateQueries(["products", page]);
    }
  }, [currentPage, page, queryClient]);

  // URL을 업데이트하는 함수
  const updateUrlWithPage = (pageNumber) => {
    // 현재 URL 경로 분석
    const path = router?.asPath?.split("?")[0]; // 쿼리 파라미터 제거
    const pathSegments = path?.split("/").filter((segment) => segment !== "");

    // 현재 경로가 /products/ 또는 /products/[ename]/ 형태인지 확인
    if (pathSegments[0] === "products") {
      let newPath = "/products/";

      // 카테고리명(Ename)이 있는 경우
      if (pathSegments.length > 1 && !isNaN(parseInt(pathSegments[1], 10))) {
        // 기존 경로가 /products/2/ 같은 형태인 경우
        newPath = `/products/${pageNumber}/`;
      } else if (pathSegments.length > 1) {
        // 기존 경로가 /products/[ename]/ 형태인 경우
        newPath = `/products/${pathSegments[1]}/${pageNumber}/`;
      } else {
        // 기본 /products/ 경로인 경우
        newPath = `/products/${pageNumber}/`;
      }

      // URL 변경 (페이지 새로고침 없이)
      router.push(newPath, undefined, { shallow: true });
    }
  };

  return (
    <>
      <Suspense>
        <Layout parent={"홈"} sub={"상품구매"} noBreadcrumb="d-none">
          <section className="container mt-15 position-relative">
            <CategorySlider />
          </section>
          <div className={"container mt-40"}>
            <P.ProductsGrid>
              {displayProducts?.map((item, i) => (
                <div key={i}>
                  <SingleProduct product={item} />
                </div>
              ))}
            </P.ProductsGrid>

            {productItems?.length > 0 && (
              <div className="d-flex w-100 justify-content-center mt-30 mb-30">
                <NoticePagination
                  prev={prev}
                  currentPage={currentPage}
                  getPaginationGroup={getPaginationGroup()}
                  next={next}
                  pages={pages}
                  nextEnd={nextEnd}
                  prevStart={prevStart}
                  handleActive={handleActive}
                />
                {pagination.length > 0 && (
                  <li className="d-none d-md-flex align-items-center ml-10">
                    {currentPage} / {pages}
                  </li>
                )}
              </div>
            )}
          </div>
        </Layout>
      </Suspense>
    </>
  );
};

export async function getServerSideProps(context) {
  const { code, type, hash } = context.query;
  const url = context.req.url;

  // SEO 데이터 설정
  let seoData = null;
  try {
    if (code) {
      const seoResponse = await seoSettingSSR(code);
      if (seoResponse?.data?.Data) {
        seoData = seoResponse.data.Data;
      }
    }
  } catch (error) {
    console.error("SEO 데이터 가져오기 실패:", error);
  }

  // URL이 /products/[ename]/[pcode] 형태인지 확인
  const isProductDetailUrl = /^\/products\/[^\/]+\/[^\/]+\/?$/.test(url);

  // 상품 상세 페이지인 경우 리다이렉트하지 않음
  if (isProductDetailUrl) {
    return {
      props: {
        seoData: seoData || null,
      },
    };
  }

  // 케이스 1 & 2: 쿼리 파라미터로 접근하는 경우
  if (code) {
    try {
      const response = await categoryRouterSSR(code);
      const category = response.data.Data;

      if (category && category.Ename) {
        // 케이스 2: 코드와 해시가 모두 있는 경우
        if (hash) {
          const targetUrl = `/products/${encodeURIComponent(
            category.Ename
          )}/${encodeURIComponent(hash)}/`;

          context.res.writeHead(301, { Location: targetUrl });
          context.res.end();
          return { props: {} };
        }

        // 케이스 1: 코드만 있는 경우
        const targetUrl = `/products/${encodeURIComponent(category.Ename)}/`;
        context.res.writeHead(301, { Location: targetUrl });
        context.res.end();
        return { props: {} };
      }
    } catch (error) {
      console.error("서버에서 category API 실패:", error);
    }
  }

  // 그 외의 경우 (예: /products/[ename])는 리다이렉트 없이 props 반환
  return {
    props: {
      seoData: seoData || null,
    },
  };
}

export default ProductsPage;

// export const getServerSideProps = getProductPageProps;
